import { create } from 'zustand';

import { i18n, LOCALE_STORAGE_KEY, readStoredLocale, type Locale } from '@/i18n';
import { applyAppearance, readCachedAppearance } from '@/lib/personalization';

export type ThemeMode = 'light' | 'dark';

export const THEME_STORAGE_KEY = 'app.theme';

/**
 * The small explicit global slice of DECISION.md §2.6: locale + dir and theme.
 * Presentation only — GEN-211 forbids anything in here from ever reaching an
 * authorization, workflow or storage decision, which is why the slice holds
 * nothing else and why nothing here is sent to the server.
 */
interface UiState {
  locale: Locale;
  theme: ThemeMode;
  setLocale: (locale: Locale) => void;
  setTheme: (theme: ThemeMode) => void;
  toggleTheme: () => void;
}

/**
 * BR-GEN-S1-08 / GEN-158: choosing ع flips the whole document right-to-left
 * (and the font, via the [dir='rtl'] rule in index.css). ONE attribute drives
 * everything — no component reads a direction prop. That attribute is
 * GEN-175's enforcement (every screen works RTL because the mirroring is the
 * document's, not each screen's), GEN-176's partner (= NFR-02: logical
 * properties everywhere), GEN-178's chassis (directional icons flip via
 * `rtl:rotate-180` and the unit.arrow dictionary), and GEN-179's trigger
 * (the Arabic face is forced by [dir='rtl'] while figures stay in LTR
 * islands).
 *
 * NFR-05 — "switching language must not lose the page a person is on, and must
 * not lose anything they have typed" — is enforced by what this function does
 * NOT do: no navigation, no reload. `i18n.changeLanguage` re-renders the
 * mounted tree in place, so the route and every controlled input survive the
 * switch.
 */
function applyLocale(locale: Locale): void {
  document.documentElement.dir = locale === 'ar' ? 'rtl' : 'ltr';
  document.documentElement.lang = locale;
  if (i18n.language !== locale) void i18n.changeLanguage(locale);
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    /* storage may be unavailable (private mode) — the choice just won't stick */
  }
}

/**
 * GEN-14: the theme button in the top bar.
 *
 * ADM-08 made this a SERVER-STORED preference (BR-ADM-08-03 — "must follow the
 * person to another computer"), and this function is now the local half of it:
 * it paints, and it mirrors into `localStorage` so the next first frame is
 * right. The record is written by whoever changed it —
 * `personalization-page.tsx` through the settings API, and `useAppearanceSync`
 * for the top bar's own button. Nothing here is the truth; see
 * `lib/personalization.ts`.
 */
function applyTheme(theme: ThemeMode): void {
  document.documentElement.classList.toggle('dark', theme === 'dark');
  try {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
    // Keep the ADM-08 cache in step, or a reload would paint the theme this
    // button just replaced.
    const cached = readCachedAppearance();
    applyAppearance({ ...cached, 'appearance.theme': theme });
  } catch {
    /* same as above */
  }
}

/**
 * NFR-13 — "there is no 'follow my device' theme. If one is wanted it must be
 * added deliberately" (BR-ADM-08-05), and ADM-08's Empty row gives a new
 * person THE DEFAULTS, whose theme is Light. This used to fall back to
 * `matchMedia('(prefers-color-scheme: dark)')` when nothing was stored — a
 * follow-the-device theme on every first visit, which nobody added
 * deliberately. A person with no stored choice now gets Light, exactly as
 * `APPEARANCE_DEFAULTS` says.
 */
function readStoredTheme(): ThemeMode {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === 'dark' || stored === 'light') return stored;
  } catch {
    /* storage unavailable — the default below is the answer anyway */
  }
  return 'light';
}

export const useUiStore = create<UiState>((set, get) => ({
  locale: readStoredLocale(),
  theme: readStoredTheme(),
  setLocale: (locale) => {
    applyLocale(locale);
    set({ locale });
  },
  setTheme: (theme) => {
    applyTheme(theme);
    set({ theme });
  },
  toggleTheme: () => {
    get().setTheme(get().theme === 'dark' ? 'light' : 'dark');
  },
}));

/**
 * Applies the persisted choices to the document before first paint.
 *
 * The APPEARANCE cache goes on first and the theme second: the cache carries
 * all six of ADM-08's choices including the theme, and `readStoredTheme` is
 * the older single-key mirror the top bar has always written. Applying theme
 * last keeps the top bar's button authoritative for the one value they share,
 * which is what a person expects from the control they last touched. Both are
 * overwritten the moment `/api/settings/effective` answers.
 */
export function initUi(): void {
  const { locale, theme } = useUiStore.getState();
  applyLocale(locale);
  applyAppearance(readCachedAppearance());
  applyTheme(theme);
}
